import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, Icon } from '@/shared/ui';

import styles from './RecordDropZone.module.scss';

const AUDIO_ACCEPT = {
  'audio/mpeg': ['.mp3'],
  'audio/mp4': ['.mp4', '.m4a'],
  'audio/wav': ['.wav'],
  'audio/ogg': ['.ogg'],
  'audio/webm': ['.webm'],
  'video/webm': ['.webm'],
};

type Props = {
  // Offers the microphone next to the upload.
  recordable: boolean;
  disabled: boolean;
  file: File | null;
  startHint?: ReactNode;
  startLabel?: string;
  starting?: boolean;
  processing?: boolean;
  children?: ReactNode;
  className?: string;
  onFile: (file: File) => void;
  onRemoveFile: () => void;
  onProcess: () => void;
  onStart?: () => void;
};

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export const RecordDropZone: FC<Props> = ({
  recordable,
  disabled,
  file,
  startHint,
  startLabel,
  starting,
  processing,
  children,
  className,
  onFile,
  onRemoveFile,
  onProcess,
  onStart,
}) => {
  const { formatMessage } = useIntl();
  const dropzone = useDropzone({
    accept: AUDIO_ACCEPT,
    multiple: false,
    maxFiles: 1,
    disabled,
    noClick: true,
    noKeyboard: true,
    onDropAccepted: (files) => onFile(files[0]),
  });

  if (file) {
    return (
      <div className={cn(styles.selected, className)}>
        <div className={styles.fileRow}>
          <span className={styles.fileIcon}>
            <Icon name="file" size={24} />
          </span>
          <span className={styles.fileText}>
            <span className={cn('p3b', styles.fileName)}>{file.name}</span>
            <span className={cn('p4', styles.fileMeta)}>
              {formatSize(file.size)}
            </span>
          </span>
          <Button
            variant="tertiary"
            size="small"
            disabled={processing}
            onClick={onRemoveFile}
          >
            <FormattedMessage id="global.delete" defaultMessage="Delete" />
          </Button>
        </div>
        <Button
          size="medium"
          block
          disabled={disabled}
          loading={processing}
          onClick={onProcess}
        >
          <FormattedMessage
            id="workspace.processFile"
            defaultMessage="Process recording"
          />
        </Button>
        {children}
      </div>
    );
  }

  return (
    <div
      {...dropzone.getRootProps({
        className: cn(
          styles.zone,
          dropzone.isDragActive && styles.dragActive,
          className,
        ),
      })}
      data-component="workspace-dropzone"
    >
      <input {...dropzone.getInputProps()} />
      {recordable ? (
        <button
          type="button"
          className={styles.recordButton}
          disabled={disabled || starting || !onStart}
          aria-label={
            startLabel ??
            formatMessage({
              id: 'workspace.startRecording',
              defaultMessage: 'Start dictation',
            })
          }
          onClick={onStart}
        >
          <Icon name="microphone" size={32} />
        </button>
      ) : (
        <span className={styles.uploadIcon} aria-hidden>
          <Icon name="file" size={28} />
        </span>
      )}
      <span className={cn('p3b', styles.hint)}>
        {startHint ??
          (recordable ? (
            <FormattedMessage
              id="workspace.clickToRecord"
              defaultMessage="Click to start recording"
            />
          ) : (
            <FormattedMessage
              id="workspace.uploadConsultation"
              defaultMessage="Upload the consultation recording"
            />
          ))}
      </span>
      <span className={styles.chooseRow}>
        <span className={cn('p4', styles.caption)}>
          {recordable ? (
            <FormattedMessage
              id="workspace.orDropAudio"
              defaultMessage="or drop an audio file here"
            />
          ) : (
            <FormattedMessage
              id="workspace.dropAudio"
              defaultMessage="Drop an audio file here"
            />
          )}
        </span>
        <Button
          variant="tertiary"
          size="small"
          disabled={disabled}
          onClick={dropzone.open}
        >
          <FormattedMessage
            id="workspace.chooseFile"
            defaultMessage="Choose file"
          />
        </Button>
      </span>
      <span className={cn('p4', styles.formats)}>MP3, WAV, M4A, OGG, WebM</span>
      {dropzone.fileRejections.length > 0 && (
        <span className={cn('p4', styles.alert)} role="alert">
          <FormattedMessage
            id="workspace.invalidFile"
            defaultMessage="Choose one file in a supported audio format."
          />
        </span>
      )}
      {children}
    </div>
  );
};
